import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DaedalusRunnerHeartbeat, DaedalusRunnerStatus } from '@pantheon/shared';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';

function isOnline(runner: DaedalusRunnerHeartbeat): boolean {
  const interval = runner.pollIntervalMs ?? 60_000;
  return Date.now() - new Date(runner.lastSeenAt).getTime() < interval * 2 + 15_000;
}

/**
 * App-wide warning when a runner's Claude Code is logged out. Runners report `needsLogin` in their
 * heartbeat (from `claude auth status`, or a run that hit the login screen) and stop claiming work
 * until someone logs in on that machine — this banner is the "go to that PC" nudge.
 */
export function RunnerLoginBanner() {
  const { get } = useApi();
  const getRef = useRef(get);
  getRef.current = get;
  const [runners, setRunners] = useState<DaedalusRunnerHeartbeat[]>([]);

  const refresh = useCallback(async () => {
    const res = await getRef.current<DaedalusRunnerStatus>('/daedalus/runner-status');
    if (res.success && res.data) {
      setRunners(res.data.runners.filter((r) => r.needsLogin && isOnline(r)));
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);
  useRefetchOnFocus(refresh);

  if (runners.length === 0) return null;
  const names = runners.map((r) => r.runnerId).join(', ');
  return (
    <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex flex-wrap items-center justify-between gap-2">
      <div>
        <span className="font-semibold">🔑 Claude needs a login on {names}.</span>{' '}
        {runners.length === 1 ? 'That runner is' : 'Those runners are'} not taking work. On the machine, run{' '}
        <code className="px-1 rounded bg-red-100">claude</code> and <code className="px-1 rounded bg-red-100">/login</code>; it picks work back up within a few minutes.
      </div>
      <Link to="/runners" className="text-red-800 font-medium underline underline-offset-2 hover:text-red-950">
        Runners
      </Link>
    </div>
  );
}
